/**
 * Regeln fuer ein neues Passwort.
 *
 * DER BEFUND (login#4 / F-47): Die Mindestlaenge war 6, ohne Staerkeanzeige.
 * "Sechs Zeichen sind fuer ein Konto, an dem die Rechnungsdaten eines
 * Unternehmens haengen, zu wenig."
 *
 * WARUM LAENGE UND NICHT ZEICHENKLASSEN: Die uebliche Forderung nach
 * Grossbuchstabe, Ziffer und Sonderzeichen erzeugt "Passwort1!" - kurz,
 * vorhersagbar und in jeder Wortliste. Das BSI empfiehlt seit 2020
 * ausdruecklich Laenge statt Komplexitaet, ebenso NIST SP 800-63B. Zwoelf
 * Zeichen sind die Grenze, ab der ein Durchprobieren nicht mehr lohnt; eine
 * Passphrase aus drei Woertern erreicht sie muehelos.
 *
 * WAS HIER NICHT PASSIERT, und das ist wichtig: Diese Regeln gelten fuer das
 * SETZEN eines Passworts - Registrierung und Zuruecksetzen. Die Anmeldemaske
 * darf sie NICHT anwenden. Wer heute ein Passwort mit acht Zeichen hat, muss
 * sich weiterhin anmelden koennen; eine Laengenpruefung dort wuerde bestehende
 * Konten aussperren, ohne die Sicherheit eines einzigen zu verbessern.
 */

/** Mindestlaenge fuer ein neu gesetztes Passwort. */
export const MIN_LAENGE = 12;

/** Ab hier gilt ein Passwort als stark. */
export const STARK_AB = 16;

/**
 * Die haeufigsten Muster, die trotz Laenge nichts taugen.
 *
 * Bewusst kurz gehalten: Eine ernsthafte Pruefung gegen geleakte Passwoerter
 * gehoert auf den Server (etwa gegen die k-Anonymity-API von Have I Been
 * Pwned). Was hier steht, faengt nur das ab, was jemand tippt, um die
 * Laengenpruefung zu ueberlisten.
 */
const SCHWACHE_MUSTER = [
  /^(.)\1+$/, //aaaaaaaaaaaa
  /^(012|123|234|345|456|567|678|789|890)+/, //123456789012
  /^(qwert|asdfg|yxcvb|password|passwort|onDispatch)/i,
];

/**
 * Wie stark ist das Passwort?
 *
 * @param {string} passwort
 * @returns {{stufe: number, text: string, farbe: string}} 0 bis 3
 */
export function staerke(passwort) {
  const wert = typeof passwort === "string" ? passwort : "";

  if (wert.length === 0) {
    return { stufe: 0, text: "", farbe: "grey" };
  }

  if (wert.length < MIN_LAENGE || SCHWACHE_MUSTER.some((m) => m.test(wert))) {
    return { stufe: 1, text: "zu schwach", farbe: "error" };
  }

  //Vielfalt zaehlt erst ab der Mindestlaenge - sie ersetzt sie nicht
  const klassen = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((m) =>
    m.test(wert)
  ).length;

  if (wert.length >= STARK_AB || klassen >= 3) {
    return { stufe: 3, text: "stark", farbe: "success" };
  }

  return { stufe: 2, text: "ausreichend", farbe: "warning" };
}

/** Ist das Passwort lang genug und kein offensichtliches Muster? */
export function istBrauchbar(passwort) {
  return staerke(passwort).stufe >= 2;
}

/** Die Meldung an einem zu schwachen Passwort. */
export const ZU_KURZ =
  "Das Passwort muss mindestens " + MIN_LAENGE + " Zeichen lang sein.";

export const ZU_EINFACH =
  "Dieses Passwort ist zu leicht zu erraten. Am besten drei zufällige Wörter.";

/**
 * Regeln fuer ein Feld, in dem ein NEUES Passwort gesetzt wird.
 *
 * Nicht fuer die Anmeldemaske - siehe Kopf dieser Datei.
 *
 * @returns {Array<Function>}
 */
export function neuesPasswortRegeln() {
  return [
    (wert) => !!wert || "Passwort wird benötigt.",
    (wert) => (wert || "").length >= MIN_LAENGE || ZU_KURZ,
    (wert) => istBrauchbar(wert) || ZU_EINFACH,
  ];
}
